import React, { useEffect, useState } from 'react';

import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useSearchParams } from 'react-router-dom';

import ThreadBarContent from './ThreadBarContent';
import useGetPublishedThreads from '../hooks/useGetPublishedThreads';
import useGetThreads from '../hooks/useGetThreads';
import { Tabs } from '@/components/molecules/Tabs';

interface ThreadsBarProps {
  goToThread: (thread_id: string) => void;
}

const PageSize = 25;

const ThreadsBar: React.FC<ThreadsBarProps> = ({ goToThread }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'previous' | 'published'>('previous');
  const caseId = searchParams.get('caseId');

  const { isLoading: isLoadingThreads, threads } = useGetThreads(caseId || '');
  const { isLoading: isLoadingPublishedThreads, threads: publishedThreads } = useGetPublishedThreads(caseId || '');
  const [displayedItems, setDisplayedItems] = useState(threads.slice(0, PageSize));
  const [hasMore, setHasMore] = useState(false);
  const activeThreadId = searchParams.get('threadId');

  const fetchMoreData = async () => {
    // 0.8s for animation spinner
    await new Promise((resolve) => setTimeout(resolve, 800));
    const dataSource = activeTab === 'previous' ? threads : publishedThreads;
    if (displayedItems.length >= dataSource.length) {
      setHasMore(false);
      return;
    }
    setDisplayedItems((prevItems) => [
      ...prevItems,
      ...dataSource.slice(prevItems.length, prevItems.length + PageSize),
    ]);
  };

  const [scrollRef] = useInfiniteScroll({
    loading: false,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreData,
  });

  useEffect(() => {
    const dataSource = activeTab === 'previous' ? threads : publishedThreads;

    setDisplayedItems(dataSource.slice(0, PageSize));
    setHasMore(dataSource.length > PageSize);
  }, [threads, publishedThreads, activeTab]);

  return (
    <>
      <div className="mb-1 h-full pt-1">
        <Tabs
          activeItem={activeTab}
          items={[
            { label: 'Previous', id: 'previous', onClick: () => setActiveTab('previous') },
            { label: 'Published', id: 'published', onClick: () => setActiveTab('published') },
          ]}
        />

        <div
          className="flex flex-col gap-1 overflow-x-clip overflow-y-auto px-1"
          style={{ height: 'calc(100% - 3rem)' }}
        >
          {isLoadingThreads || isLoadingPublishedThreads ? (
            <div className="flex w-52 flex-col justify-center overflow-hidden">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="my-1 h-8 w-52 animate-pulse rounded bg-gray-100 px-2"></div>
              ))}
            </div>
          ) : (
            <ThreadBarContent
              activeTab={activeTab}
              displayedItems={displayedItems}
              hasMore={hasMore}
              scrollRef={scrollRef}
              activeThreadId={activeThreadId}
              goToThread={goToThread}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(ThreadsBar);
